import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useUpdateJobFinancials } from '@/hooks/useJobs';
import { JobWithRelations } from '@/types';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useCompanyTax } from '@/hooks/useCompanyTax';
import { formatTaxSuffix } from '@/utils/tax';

const financialsSchema = z.object({
  invoice_total: z.string().optional(),
  toll_cost: z.string().optional(),
});

type FinancialsFormData = z.infer<typeof financialsSchema>;

interface FinancialsFormProps {
  job: JobWithRelations;
}

export function FinancialsForm({ job }: FinancialsFormProps) {
  const updateFinancials = useUpdateJobFinancials();
  const tax = useCompanyTax();

  const form = useForm<FinancialsFormData>({
    resolver: zodResolver(financialsSchema),
    defaultValues: {
      invoice_total: job.invoice_total?.toString() || '',
      toll_cost: job.toll_cost?.toString() || '0',
    },
  });

  const handleSubmit = (data: FinancialsFormData) => {
    updateFinancials.mutate({
      jobId: job.id,
      invoice_total: data.invoice_total
        ? parseFloat(data.invoice_total)
        : undefined,
      toll_cost: data.toll_cost ? parseFloat(data.toll_cost) : undefined,
    });
  };

  return (
    <div className="space-y-4">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="invoice_total"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Actual Job Revenue{tax.enabled ? ` (${formatTaxSuffix(tax)})` : ''}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="toll_cost"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Toll Cost</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" disabled={updateFinancials.isPending}>
            {updateFinancials.isPending ? 'Updating...' : 'Update Financials'}
          </Button>
        </form>
      </Form>

    </div>
  );
}
